import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';

interface Toast {
  id: string;
  type: 'entry' | 'exit' | 'quick_patrol' | 'double_hit' | 'checkpoint_rule' | 'checkpoint_scan';
  actorName: string;
  actionText?: string;
  siteName: string;
  timestamp: number;
}

interface NotificationSystemProps {
  children: React.ReactNode;
}

const TYPE_META: Record<Toast['type'], { label: string; action: string }> = {
  entry:           { label: 'Site Entry',       action: 'entered'               },
  exit:            { label: 'Site Exit',         action: 'exited'                },
  quick_patrol:    { label: 'Quick Patrol',      action: 'quick patrol at'       },
  double_hit:      { label: 'Double Hit',        action: 'double hit at'         },
  checkpoint_rule: { label: 'Rule Violation',    action: 'violated a checkpoint rule at' },
  checkpoint_scan: { label: 'Checkpoint Scan',   action: 'scanned at'            },
};

function sanitizeGuardName(raw: unknown): string {
  const guard = String(raw || '').trim();
  if (!guard) return '';
  const lower = guard.toLowerCase();
  if (lower === 'unknown' || lower === 'unknown guard' || lower === 'unknown location') return '';
  if (guard.includes('visit.guardName') || guard.includes('||')) return '';
  return guard;
}

function normalizeSiteNameForMatch(name: string): string {
  return (name || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[:\-]\s*\d+$/, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripCheckpointSuffix(name: string): string {
  return (name || '').replace(/[:\-]\s*\d+\s*$/, '').trim();
}

function checkpointLabelMatchesSite(checkpointLabel: string, siteName: string): boolean {
  const normalizedSite = normalizeSiteNameForMatch(siteName);
  if (!normalizedSite) return false;

  const normalizedCheckpoint = normalizeSiteNameForMatch(checkpointLabel);
  if (
    normalizedCheckpoint &&
    (normalizedCheckpoint === normalizedSite ||
      normalizedCheckpoint.includes(normalizedSite) ||
      normalizedSite.includes(normalizedCheckpoint))
  ) {
    return true;
  }

  const checkpointBase = normalizeSiteNameForMatch(stripCheckpointSuffix(checkpointLabel));
  if (
    checkpointBase &&
    (checkpointBase === normalizedSite ||
      checkpointBase.includes(normalizedSite) ||
      normalizedSite.includes(checkpointBase))
  ) {
    return true;
  }

  return false;
}

function resolveCheckpointScanSiteName(args: {
  rawSiteName: string;
  rawCheckpointName: string;
  candidateSites: string[];
}): string {
  const sourceLabels = [args.rawSiteName, args.rawCheckpointName]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  if (sourceLabels.length === 0) return 'Unknown Site';

  for (const label of sourceLabels) {
    const exact = args.candidateSites.find(
      (site) => normalizeSiteNameForMatch(site) === normalizeSiteNameForMatch(label)
    );
    if (exact) return exact;
  }

  for (const label of sourceLabels) {
    const matched = args.candidateSites.find((site) => checkpointLabelMatchesSite(label, site));
    if (matched) return matched;
  }

  const stripped = stripCheckpointSuffix(sourceLabels[0]);
  return stripped || sourceLabels[0];
}

const NotificationSystem: React.FC<NotificationSystemProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seenEventKeys = useRef<Set<string>>(new Set());
  const initialLoadDone = useRef(false);

  const events =
    (useQuery((api as any).homeEvents.getRecentHomeEvents, {
      limit: 80,
    }) as any[] | undefined) || [];
  const activeSites =
    (useQuery(api.sites.getActiveBasic, {}) as any[] | undefined) || [];
  const checkpointScanCandidateSites = useMemo(
    () =>
      activeSites
        .map((site: any) => String(site?.name || '').trim())
        .filter((name: string) => name.length > 0)
        .sort((a: string, b: string) => b.length - a.length),
    [activeSites]
  );

  const toNotificationKey = (row: any) => String(row?.eventKey || row?._id || '').trim();

  const toToast = (row: any): Toast | null => {
    const type = String(row?.eventType || '').trim().toLowerCase();
    if (
      type !== 'entry' &&
      type !== 'exit' &&
      type !== 'quick_patrol' &&
      type !== 'double_hit' &&
      type !== 'checkpoint_rule' &&
      type !== 'checkpoint_scan'
    ) {
      return null;
    }

    const id = toNotificationKey(row);
    if (!id) return null;

    const rawSiteName = String(row?.siteName || '').trim();
    const siteName =
      type === 'checkpoint_scan'
        ? resolveCheckpointScanSiteName({
            rawSiteName,
            rawCheckpointName: String(row?.checkpointName || '').trim(),
            candidateSites: checkpointScanCandidateSites,
          })
        : rawSiteName || 'Unknown Site';
    const guardName = sanitizeGuardName(row?.guardName);
    const vehicleName = String(row?.vehicleName || '').trim();

    let actorName = 'Guard';
    if (type === 'entry' || type === 'exit') {
      actorName = guardName || vehicleName || 'Vehicle';
    } else {
      actorName = guardName || vehicleName || 'Guard';
    }

    return {
      id,
      type: type as Toast['type'],
      actorName,
      siteName,
      timestamp: Number(row?.timestamp || Date.now()),
    };
  };

  useEffect(() => {
    if (events.length === 0) return;

    if (!initialLoadDone.current) {
      events.forEach((eventRow: any) => {
        const key = toNotificationKey(eventRow);
        if (key) seenEventKeys.current.add(key);
      });
      initialLoadDone.current = true;
      return;
    }

    const newEvents = events
      .filter((eventRow: any) => {
        const key = toNotificationKey(eventRow);
        return !!key && !seenEventKeys.current.has(key);
      })
      .sort((a: any, b: any) => Number(a?.timestamp || 0) - Number(b?.timestamp || 0));

    newEvents.forEach((eventRow: any) => {
      const key = toNotificationKey(eventRow);
      if (!key) return;
      seenEventKeys.current.add(key);

      const toast = toToast(eventRow);
      if (!toast) return;

      setToasts(prev => [toast, ...prev].slice(0, 5));

      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toast.id));
      }, 6000);
    });
  }, [events]);


  return (
    <>
      {children}

      <div className="nt-container">
        {toasts.map((toast, index) => {
          const meta = TYPE_META[toast.type];
          return (
            <div
              key={toast.id}
              className={`nt-toast nt-toast--${toast.type}`}
              style={{ animationDelay: `${index * 40}ms` }}
            >
              <div className="nt-stripe" />
              <div className="nt-body">
                <p className="nt-actor">{toast.actorName}</p>
                <p className="nt-desc">
                  {meta.action} <span className="nt-site">{toast.siteName}</span>
                </p>
              </div>
              <div className={`nt-progress nt-progress--${toast.type}`} />
            </div>
          );
        })}
      </div>
    </>
  );
};

export default NotificationSystem;
